import {
  type CreverseGuestAdmission,
  type Guest,
  guestApi,
  type GuestSyncLatest,
} from '@mathflat/domain/Guest'
import type { QueryClient } from '@tanstack/react-query'

import { localStorageService } from '@/service/storage.service'

import queryClient from './queryClient'

export const guestsQuery = {
  queryKey: ['guests'] as const,
  queryFn: () => guestApi.getGuests(),
  // 비재원생은 sync 로만 갱신되므로 길게 캐시해 중복 GET 을 막는다.
  staleTime: 1000 * 60,
}

export const admissionsQuery = {
  // 'guests' prefix 를 피한다 — invalidateGuests(['guests'])의 prefix 매칭에 휘말리지 않도록 별도 키.
  queryKey: ['guest-admissions'] as const,
  queryFn: () => guestApi.getCreverseGuestAdmissions(),
  staleTime: 1000 * 60,
}

export const latestGuestSyncQuery = {
  // 'guests' prefix 를 쓰면 invalidateQueries(['guests'])의 prefix 매칭에 휘말려 echo 로 채운 캐시가 즉시 refetch 로 덮인다
  queryKey: ['guest-sync', 'latest'] as const,
  queryFn: () => guestApi.getLatestGuestSync(),
}

export const guestController = {
  prefetchGuests: () => queryClient.prefetchQuery(guestsQuery),
  fetchGuests: () => queryClient.fetchQuery(guestsQuery),
  getCachedGuests: () => queryClient.getQueryData<Guest[]>(guestsQuery.queryKey),
  invalidateGuests: () => queryClient.invalidateQueries({ queryKey: guestsQuery.queryKey }),
  // 화면이 구독하는 클라이언트에서 캐시를 갱신해야 하므로 client 를 호출부에서 받는다.
  syncGuests: async (client: QueryClient) => {
    const ssoToken = localStorageService.getItem('CREVERSE_SSO_TOKEN')
    // 크레버스 세션이 없으면 빈 토큰으로 400 → 강제 로그아웃되는 부작용을 피해 사전 차단한다
    if (!ssoToken) {
      return Promise.reject(new Error('CREVERSE_SSO_TOKEN_NOT_FOUND'))
    }
    const { syncedAt } = await guestApi.syncGuests({ ssoToken })
    // GET 한 번 결과를 두 캐시에 직접 반영한다 — invalidate 로는 클라이언트마다 개별 refetch 해 중복 GET 이 났다.
    const [guests, admissions] = await Promise.all([
      guestApi.getGuests(),
      guestApi.getCreverseGuestAdmissions(),
    ])
    client.setQueryData<Guest[]>(guestsQuery.queryKey, guests)
    queryClient.setQueryData<Guest[]>(guestsQuery.queryKey, guests)
    client.setQueryData<CreverseGuestAdmission[]>(admissionsQuery.queryKey, admissions)
    queryClient.setQueryData<CreverseGuestAdmission[]>(admissionsQuery.queryKey, admissions)
    // 명단 갱신까지 성공한 뒤 SUCCESS 로 기록한다 — guestApi.getGuests 실패 시 동기화 상태와 캐시가 어긋나지 않도록.
    client.setQueryData<GuestSyncLatest>(latestGuestSyncQuery.queryKey, {
      status: 'SUCCESS',
      syncedAt,
    })
  },
}
